/**
 * Settings parts (Figma 09 · Settings and connectors, 02 States "State board":
 * Page head, Index row, Detail head, Fact line, Section header, Note, Meter,
 * Dialog rows). Built on the kit; every page in components/settings/pages is
 * composed from these.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
  type ReactNode,
  type SVGProps,
} from "react";
import { Link } from "react-router";
import { animate, useReducedMotion } from "motion/react";
import { ChevronLeft, ChevronRight, Info, TriangleAlert } from "lucide-react";
import { cn } from "@/utils/helpers";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { KitButton } from "../../kit/Button";
import { KitInput } from "../../kit/Input";
import { KitConsequenceRow, KitModal } from "../../kit/Modal";
import { initials } from "./settingsFormat";

type Icon = FunctionComponent<SVGProps<SVGSVGElement>>;

/* ── Marks ──────────────────────────────────────────────────────────────── */

/** A person: their photo, or two letters on the tonal disc (library Avatar). */
export function PersonMark({
  name,
  picture,
  size = 36,
}: {
  name: string | null | undefined;
  picture?: string | null;
  size?: 22 | 32 | 36 | 48 | 56;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const font = { 22: 9.5, 32: 11.5, 36: 12.5, 48: 16, 56: 19 }[size];
  const showPicture = !!picture && failed !== picture;
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className="grid shrink-0 place-items-center overflow-hidden rounded-full bg-co-tonal"
    >
      {showPicture ? (
        <img
          src={picture as string}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setFailed(picture ?? null)}
          className="size-full object-cover"
        />
      ) : (
        <span style={{ fontSize: font }} className="font-bold leading-none text-co-text2">
          {initials(name)}
        </span>
      )}
    </span>
  );
}

/** A workspace: its two letters on the solid tile (library Brand tile). */
export function ScopeTile({ name, size = 36 }: { name: string; size?: 22 | 36 | 56 }) {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className="grid shrink-0 place-items-center rounded-full border border-co-line2 bg-co-surface-solid"
    >
      <span
        style={{ fontSize: { 22: 10, 36: 15, 56: 22 }[size] }}
        className="font-section font-extrabold leading-none tracking-[0.3px] text-co-text"
      >
        {initials(name)}
      </span>
    </span>
  );
}

/** An app or setting: its glyph on the solid disc. */
export function GlyphTile({ icon: Glyph, size = 36 }: { icon: Icon; size?: 36 | 56 }) {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className="grid shrink-0 place-items-center rounded-full border border-co-line2 bg-co-surface-solid"
    >
      <Glyph className={size === 56 ? "size-6 text-co-text2" : "size-4 text-co-text2"} />
    </span>
  );
}

/* ── Page frame ─────────────────────────────────────────────────────────── */

export function SettingsPage({ children }: { children: ReactNode }) {
  return <div className="flex w-full min-w-0 flex-col gap-6">{children}</div>;
}

/** Settings / Page head: mark, scope, page, Change, the one action; the status sentence below. */
export function PageHead({
  mark,
  scopeName,
  title,
  onChange,
  action,
  status,
}: {
  mark: ReactNode;
  scopeName?: string | null;
  title: string;
  /** "Change": opens the workspace or brand switcher. */
  onChange?: () => void;
  action?: ReactNode;
  status?: ReactNode;
}) {
  return (
    <header className="flex w-full flex-col gap-2.5">
      <div className="flex w-full flex-wrap items-center gap-3">
        {mark}
        <h1 className="flex min-w-0 flex-wrap items-baseline gap-x-3 font-section text-[26px] font-bold leading-[1.2] tracking-normal md:text-[30px]">
          {scopeName ? <span className="text-co-text">{scopeName}</span> : null}
          <span className={scopeName ? "text-co-muted" : "text-co-text"}>{title}</span>
        </h1>
        {onChange ? (
          <button
            type="button"
            onClick={onChange}
            className="rounded-pill px-1 text-[13px] text-co-muted transition-colors hover:text-co-text"
          >
            Change
          </button>
        ) : null}
        <span className="min-w-0 flex-1" />
        {action}
      </div>
      {status ? <p className="max-w-[640px] text-[13.5px] leading-[1.5] text-co-text2">{status}</p> : null}
    </header>
  );
}

/** A single-column page's panel (Detail · 760). */
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cn("flex w-full max-w-[760px] flex-col gap-5 rounded-panel bg-co-surface p-5 md:p-7", className)}>
      {children}
    </section>
  );
}

/* ── Index and detail ───────────────────────────────────────────────────── */

const SplitContext = createContext<{ push: () => void } | null>(null);

/**
 * Settings / Split: the index (320) and the open thing. On a phone the index
 * is the screen and a row pushes its detail, with a way back (Flows · Phone).
 */
export function Split({
  index,
  detail,
  backLabel = "Back",
}: {
  index: ReactNode;
  detail: ReactNode;
  backLabel?: string;
}) {
  const isPhone = useMediaQuery("(max-width: 639px)");
  const [pushed, setPushed] = useState(false);
  const top = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pushed) top.current?.scrollIntoView({ block: "start" });
  }, [pushed]);

  if (isPhone) {
    return (
      <SplitContext.Provider value={{ push: () => setPushed(true) }}>
        <div ref={top} className="flex w-full flex-col gap-3">
          {pushed ? (
            <>
              <button
                type="button"
                onClick={() => setPushed(false)}
                className="inline-flex h-8 items-center gap-1 self-start text-[13px] font-medium text-co-accent"
              >
                <ChevronLeft aria-hidden className="size-3.5" />
                {backLabel}
              </button>
              {detail}
            </>
          ) : (
            index
          )}
        </div>
      </SplitContext.Provider>
    );
  }

  return (
    <SplitContext.Provider value={null}>
      <div className="flex w-full items-start gap-5">
        <div className="w-[260px] shrink-0 lg:w-[320px]">{index}</div>
        <div className="min-w-0 flex-1">{detail}</div>
      </div>
    </SplitContext.Provider>
  );
}

export function IndexCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <nav className={cn("flex w-full flex-col gap-0.5 rounded-panel bg-co-surface p-2", className)}>{children}</nav>
  );
}

export function DetailCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cn("flex w-full flex-col gap-5 rounded-panel bg-co-surface p-5 md:p-7", className)}>
      {children}
    </section>
  );
}

export function GroupLabel({ children }: { children: ReactNode }) {
  return <p className="px-3 pb-1 pt-2.5 text-[12px] font-semibold text-co-muted">{children}</p>;
}

/** Settings / Index row: lead, name, one line, chevron (or a status glyph). */
export function IndexRow({
  lead,
  title,
  sub,
  subTone = "muted",
  trail,
  active,
  onPress,
}: {
  lead: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  subTone?: "muted" | "alert";
  trail?: ReactNode;
  active?: boolean;
  onPress: () => void;
}) {
  const split = useContext(SplitContext);
  return (
    <button
      type="button"
      aria-current={active ? "true" : undefined}
      onClick={() => {
        onPress();
        split?.push();
      }}
      className={cn(
        "flex w-full items-center gap-3 rounded-row px-3 py-2.5 text-left transition-colors",
        active ? "bg-co-accent-dim" : "hover:bg-co-hover",
      )}
    >
      {lead}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[13.5px] font-semibold text-co-text">{title}</span>
        {sub ? (
          <span className={cn("text-[12px] leading-[1.4]", subTone === "alert" ? "text-co-alert-ink" : "text-co-muted")}>
            {sub}
          </span>
        ) : null}
      </span>
      {trail ?? <ChevronRight aria-hidden className="size-3.5 shrink-0 text-co-faint" />}
    </button>
  );
}

/** Settings / Detail head: avatar 56, Barlow name, one line, then status and actions.
 *  The status drops under the account once the row is under 36rem, before the
 *  name and handle run into it. */
export function DetailHead({
  lead,
  title,
  sub,
  aside,
}: {
  lead: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="@container flex w-full flex-wrap items-center gap-4">
      {lead}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <h2 className="truncate font-section text-[28px] font-bold leading-[34px] tracking-normal text-co-text">{title}</h2>
        {sub ? <p className="truncate text-[13px] leading-[18px] text-co-muted">{sub}</p> : null}
      </div>
      {aside ? <div className="flex shrink-0 flex-wrap items-center gap-3 @max-[36rem]:basis-full">{aside}</div> : null}
    </div>
  );
}

/**
 * Settings / State: a status beside a detail head ("✓ Connected"). 13 SemiBold,
 * muted unless alert. Settings M07: a change of status cross-fades in 200ms;
 * `turning` marks (Connecting, Checking) turn once every 1.2s, and stay still
 * under reduced motion with their words.
 */
export function StatusLabel({
  icon: Glyph,
  children,
  tone = "default",
  turning = false,
}: {
  icon?: Icon;
  children: ReactNode;
  tone?: "default" | "alert" | "muted";
  turning?: boolean;
}) {
  return (
    <span
      key={typeof children === "string" ? children : undefined}
      className={cn(
        "inline-flex items-center gap-1.5 text-[13px] font-semibold [animation:co-content-in_200ms_ease-out_backwards]",
        tone === "alert" ? "text-co-alert-ink" : "text-co-muted",
      )}
    >
      {Glyph ? (
        <Glyph aria-hidden className={cn("size-[15px] shrink-0", turning && "animate-[spin_1.2s_linear_infinite] motion-reduce:animate-none")} />
      ) : null}
      {children}
    </span>
  );
}

export function Facts({ children }: { children: ReactNode }) {
  return <dl className="flex w-full flex-col">{children}</dl>;
}

/** Settings / Fact line: label 150, value. */
export function Fact({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className="flex w-full flex-col gap-1 py-[9px] leading-[1.45] sm:flex-row sm:gap-4">
      <dt className={cn("shrink-0 text-[12.5px] text-co-muted", wide ? "sm:w-[170px]" : "sm:w-[150px]")}>{label}</dt>
      <dd className="min-w-0 flex-1 text-[13.5px] font-medium text-co-text">{children}</dd>
    </div>
  );
}

/** Section header: Barlow 20, a muted count, the rule under it. */
export function SectionHead({ title, meta, aside }: { title: string; meta?: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex w-full items-end gap-3 border-b border-co-line pb-3">
      <h3 className="font-section text-[20px] font-bold leading-none tracking-normal text-co-text">{title}</h3>
      {meta ? <span className="text-[12.5px] text-co-muted">{meta}</span> : null}
      <span className="flex-1" />
      {aside}
    </div>
  );
}

/** Text action: an accent label with an optional 14px icon. */
export function TextAction({
  icon: Glyph,
  children,
  onPress,
  to,
  tone = "accent",
  disabled,
}: {
  icon?: Icon;
  children: ReactNode;
  onPress?: () => void;
  to?: string;
  tone?: "accent" | "danger";
  disabled?: boolean;
}) {
  const className = cn(
    "inline-flex h-8 items-center gap-1.5 self-start rounded-pill px-1 text-[13px] font-medium transition-colors disabled:opacity-40",
    tone === "danger" ? "text-co-alert-ink hover:opacity-80" : "text-co-accent hover:text-co-accent-ink",
  );
  const body = (
    <>
      {Glyph ? <Glyph aria-hidden className="size-3.5 shrink-0" /> : null}
      {children}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cn("unstyled-link", className)}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onPress} disabled={disabled} className={className}>
      {body}
    </button>
  );
}

/** Inline note: alert (red edge + triangle) or info (hairline + i). Sits in the flow. */
export function Note({
  tone = "info",
  icon,
  children,
  actions,
}: {
  tone?: "alert" | "info";
  icon?: Icon;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const Glyph = icon ?? (tone === "alert" ? TriangleAlert : Info);
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-3 rounded-card border px-3.5 py-3",
        tone === "alert" ? "border-co-alert-ink" : "border-co-line2",
      )}
    >
      <div className="flex items-start gap-2.5">
        <Glyph
          aria-hidden
          className={cn("mt-[3px] size-[15px] shrink-0", tone === "alert" ? "text-co-alert-ink" : "text-co-muted")}
        />
        <div className="min-w-0 flex-1 text-[14px] leading-[1.5] text-co-text2">{children}</div>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-3 pl-[25px]">{actions}</div> : null}
    </div>
  );
}

/** Settings / Note: the quiet line at the foot of a panel. */
export function QuietNote({ children }: { children: ReactNode }) {
  return <p className="text-[12.5px] leading-[1.5] text-co-muted">{children}</p>;
}

/* ── Meters ─────────────────────────────────────────────────────────────── */

export type MeterTone = "default" | "alert";

export function meterTone(pct: number): MeterTone {
  return pct >= 80 ? "alert" : "default";
}

/**
 * Settings M11, the meter fills: on a visit the bar grows from zero to its
 * value in 480ms, easing out, and the figure counts up with it; a later change
 * moves from the old value, never from zero. Under reduced motion both appear
 * at their value. The tone follows the moving value, so passing 80% turns the
 * bar and the figure to alert at that moment.
 */
function useMeterValue(target: number): number {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(() => (reduced ? target : 0));
  const current = useRef(reduced ? target : 0);
  useEffect(() => {
    if (reduced) {
      current.current = target;
      return;
    }
    const controls = animate(current.current, target, {
      duration: 0.48,
      ease: "easeOut",
      onUpdate: (next) => {
        current.current = next;
        setValue(next);
      },
    });
    return () => controls.stop();
  }, [target, reduced]);
  return reduced ? target : value;
}

/** Allowance meter: the big percentage, its caption, the track, the scale. */
export function Meter({
  pct,
  caption,
  left,
  right,
}: {
  pct: number;
  caption: string;
  left: ReactNode;
  right: ReactNode;
}) {
  const value = useMeterValue(Math.min(100, Math.max(0, pct)));
  const tone = meterTone(value);
  const shown = Math.round(value);
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <p
          className={cn(
            "font-section text-[56px] font-bold leading-[0.9] md:text-[64px]",
            tone === "alert" ? "text-co-alert-ink" : "text-co-text",
          )}
        >
          {shown}%
        </p>
        <p className="pb-1 text-[15px] text-co-muted">{caption}</p>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-[4px] bg-co-line2" role="presentation">
        <div
          className={cn("h-full w-full origin-left rounded-[4px]", tone === "alert" ? "bg-co-alert-ink" : "bg-co-text")}
          style={{ transform: `scaleX(${value / 100})` }}
        />
      </div>
      <div className="flex w-full items-center gap-3">
        <span className="text-[13px] font-medium text-co-text2">{left}</span>
        <span className="flex-1" />
        <span className="text-[13px] text-co-muted">{right}</span>
      </div>
    </div>
  );
}

/** One person's allowance row on the workspace Usage page. */
export function PersonMeter({
  lead,
  name,
  meta,
  metaTone = "muted",
  amount,
  pct,
}: {
  lead: ReactNode;
  name: string;
  meta: ReactNode;
  metaTone?: "muted" | "alert";
  amount: string;
  pct: number;
}) {
  const value = useMeterValue(Math.min(100, Math.max(0, pct)));
  const tone = meterTone(value);
  const shown = Math.round(value);
  return (
    <div className="flex w-full items-start gap-3 rounded-[8px] p-3">
      {lead}
      <div className="flex min-w-0 flex-1 flex-col gap-[9px]">
        <div className="flex w-full flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[14px] font-semibold text-co-text">{name}</span>
          <span className={cn("text-[12.5px]", metaTone === "alert" ? "text-co-alert-ink" : "text-co-muted")}>{meta}</span>
          <span className="flex-1" />
          <span className="text-[13px] text-co-text2">{amount}</span>
          <span className={cn("w-11 text-right text-[13px] font-bold", tone === "alert" ? "text-co-alert-ink" : "text-co-text")}>
            {shown}%
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-[3px] bg-co-line2" role="presentation">
          <div
            className={cn("h-full w-full origin-left rounded-[3px]", tone === "alert" ? "bg-co-alert-ink" : "bg-co-text2")}
            style={{ transform: `scaleX(${value / 100})` }}
          />
        </div>
      </div>
    </div>
  );
}

/* ── Dialogs ────────────────────────────────────────────────────────────── */

export function DialogFooter({
  cancelLabel = "Cancel",
  confirmLabel,
  onCancel,
  onConfirm,
  disabled,
  danger,
  type = "button",
}: {
  cancelLabel?: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm?: () => void;
  disabled?: boolean;
  danger?: boolean;
  type?: "button" | "submit";
}) {
  return (
    <div className="flex items-center justify-end gap-3 pt-4">
      <KitButton type="button" variant="ghost" onClick={onCancel}>
        {cancelLabel}
      </KitButton>
      <KitButton type={type} variant={danger ? "danger" : "primary"} onClick={onConfirm} isDisabled={disabled}>
        {confirmLabel}
      </KitButton>
    </div>
  );
}

export type Consequence = { icon: Icon; text: ReactNode };

/**
 * A confirm (Figma 09 · Confirm · …): the question, what happens, one row per
 * consequence, and for permanent removals the name typed back.
 */
export function ConfirmDialog({
  title,
  description,
  consequences = [],
  typeToConfirm,
  confirmLabel,
  cancelLabel = "Cancel",
  danger = true,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  consequences?: Consequence[];
  /** The word to type back; the confirm stays off until it matches. */
  typeToConfirm?: string;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  const [typed, setTyped] = useState("");
  const matches = !typeToConfirm || typed.trim().toLowerCase() === typeToConfirm.trim().toLowerCase();
  return (
    <KitModal
      isOpen
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={title}
      description={description}
      modalClassName="!w-[min(calc(100vw-2rem),520px)] !max-w-[520px]"
    >
      <form
        className="flex w-full flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (matches) onConfirm();
        }}
      >
        {consequences.map((c, i) => (
          <KitConsequenceRow key={i} icon={c.icon}>
            {c.text}
          </KitConsequenceRow>
        ))}
        {children}
        {typeToConfirm ? (
          <KitInput
            label={`Type ${typeToConfirm} to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={typeToConfirm}
            autoComplete="off"
            autoFocus
          />
        ) : null}
        <DialogFooter
          type="submit"
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
          onCancel={onClose}
          disabled={!matches}
          danger={danger}
        />
      </form>
    </KitModal>
  );
}

/* ── Loading ────────────────────────────────────────────────────────────── */

function SkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex items-center gap-3 border-b border-co-line px-3 py-[13px] last:border-b-0">
          <span className="h-2.5 w-[42%] animate-pulse rounded-full bg-co-hover" />
          <span className="flex-1" />
          <span className="h-2.5 w-6 animate-pulse rounded-full bg-co-hover" />
        </div>
      ))}
    </>
  );
}

function LoadingCaption({ children }: { children: string }) {
  return (
    <p
      role="status"
      aria-live="polite"
      className="flex items-center gap-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-co-muted"
    >
      <span aria-hidden className="co-livedot inline-block size-1.5 rounded-full bg-co-live" />
      {children}
    </p>
  );
}

/** States · Loading: skeleton index rows and the caption in the detail (Figma 09 · People / 15). */
export function SplitLoading({ caption, rows = 6 }: { caption: string; rows?: number }) {
  return (
    <Split
      index={
        <IndexCard>
          <SkeletonRows count={rows} />
        </IndexCard>
      }
      detail={
        <DetailCard>
          <LoadingCaption>{caption}</LoadingCaption>
        </DetailCard>
      }
    />
  );
}

export function CardLoading({ caption, rows = 4 }: { caption: string; rows?: number }) {
  return (
    <Card>
      <LoadingCaption>{caption}</LoadingCaption>
      <div className="flex flex-col">
        <SkeletonRows count={rows} />
      </div>
    </Card>
  );
}
